import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { store } from '@/common/root';
import api from '@/common/utils/api';
import NotFoundError from '@/common/utils/errors/NotFoundError';
import CancellationError from '@/common/utils/errors/CancellationError';
import { DOMAIN_PROPS_KEYS, DOMAIN_PROPS_ACCESS_TYPES } from '@/common/constants/domainProps.constants';
import { DEFAULT_DOMAIN_NAME } from '@/common/constants/domains.constants';
import { TotpConfig } from '@schema';

const TOTP_CONFIG_API_URL = '/api/v2/accounts/totp/config/';
const FETCH_PROP_ACTION = 'domainProps/FETCH_PROP';
const TOTP_SETTINGS_PROP = {
  key: DOMAIN_PROPS_KEYS.TOTP_SETTINGS,
  domain: DEFAULT_DOMAIN_NAME
};

// A missing prop means the setting was never touched: keep the pre-setting behavior.
export const TOTP_NOTIFY_DEFAULT = true;
export const TOTP_USER_SETUP_DEFAULT = true;

type TotpSetting = {
  enabled?: boolean
};

export type TotpSettings = {
  notify?: TotpSetting
  userSetup?: TotpSetting
};

export const useTotpSettingsStore = defineStore('totpSettings', () => {
  const generalTotp = ref<TotpConfig | null>(null);
  const totpSettings = ref<TotpSettings | null>(null);
  const totpSettingsLoaded = computed(() => totpSettings.value !== null);

  const totpNotify = computed<boolean | null>(() => (totpSettingsLoaded.value
    ? totpSettings.value?.notify?.enabled ?? TOTP_NOTIFY_DEFAULT
    : null));
  const totpUserSetup = computed<boolean | null>(() => (totpSettingsLoaded.value
    ? totpSettings.value?.userSetup?.enabled ?? TOTP_USER_SETUP_DEFAULT
    : null));

  // Only an explicit false hides anything: an unresolved value means the setting was never
  // turned off, so both keep the pre-setting behavior. A failed fetch must never strip away
  // the user's only way to configure or reset their own 2FA.
  const isTotpNotifyEnabled = computed(() => totpNotify.value !== false);
  const isTotpUserSetupEnabled = computed(() => totpUserSetup.value !== false);

  const fetchGeneralTotp = async (): Promise<TotpConfig | null> => {
    try {
      const response = await api.get<TotpConfig>(TOTP_CONFIG_API_URL);
      generalTotp.value = response.data;
      return response.data;
    } catch (error) {
      if (error instanceof NotFoundError) {
        generalTotp.value = null;
        return null;
      }
      throw error;
    }
  };

  const setGeneralTotp = async (mandatory: boolean): Promise<TotpConfig> => {
    const response = await api.put<TotpConfig>(TOTP_CONFIG_API_URL, { mandatory });
    generalTotp.value = response.data;
    return response.data;
  };

  const setDomainTotp = (domainId: string, enabled: boolean) => store.dispatch('domains/domains/UPDATE', {
    id: domainId,
    body: { totp_mandatory: enabled },
  });

  const fetchTotpSettings = async (force = false): Promise<TotpSettings> => {
    if (!force && totpSettingsLoaded.value) {
      return totpSettings.value ?? {};
    }

    try {
      const value = force
        ? await store.$takeLast(FETCH_PROP_ACTION, TOTP_SETTINGS_PROP)
        : await store.$takeFirst(FETCH_PROP_ACTION, TOTP_SETTINGS_PROP);
      totpSettings.value = (value ?? {}) as TotpSettings;
    } catch (error) {
      if (error instanceof CancellationError) {
        return totpSettings.value ?? {};
      }
      totpSettings.value = {};
    }

    return totpSettings.value;
  };

  const resetTotpSettings = () => {
    store.$abortEffect(FETCH_PROP_ACTION, TOTP_SETTINGS_PROP);
    totpSettings.value = null;
  };

  const setTotpSettings = ({ notify, userSetup }: { notify: boolean, userSetup: boolean }) => {
    const value: TotpSettings = {
      notify: { enabled: notify },
      userSetup: { enabled: userSetup }
    };

    return store.dispatch('domainProps/SET', {
      key: DOMAIN_PROPS_KEYS.TOTP_SETTINGS,
      value,
      domain: DEFAULT_DOMAIN_NAME,
      access: DOMAIN_PROPS_ACCESS_TYPES.PUBLIC
    }).then((res) => {
      totpSettings.value = value;
      return res;
    });
  };

  return {
    generalTotp,
    totpSettings,
    totpSettingsLoaded,
    totpNotify,
    totpUserSetup,
    isTotpNotifyEnabled,
    isTotpUserSetupEnabled,
    fetchGeneralTotp,
    setGeneralTotp,
    setDomainTotp,
    fetchTotpSettings,
    resetTotpSettings,
    setTotpSettings
  };
});

export default useTotpSettingsStore;
